import router from '../../router';
import { timeFormat, dateTimeFormat } from '@/utils/date.js';
import { normalizeType } from '@/utils/mirrorFormatter.js';
import { checkMirrorHasUpdated } from '../../utils/checkMirrorHasUpdated';

const state = {
  requestFailed: false,
  systemFoldersMirrorHistoric: [],
  systemHistoricMirror: [],
  systemHistoricMirrorAfter: null,
  canLoadSystemHistoricMirror: true,
  loadingSystemHistoricMirror: false,
  errorGetMirrorHistoric: false,
  selectNameHistoricMirror: '',
  loadingFoldersMirror: false,
  mirrorProcessingDateGroup: [],
  loadingMirrorProcessingDateGroup: false,
  errorMirrorProcessingDateGroup: false,
  activeFilters: ['studio', 'vt', 'live', 'screen', 'others'],
  programs: [],
  loadingUpdateMirror: false,
  programmedActivities: [],
  programmedActivitiesTotal: 1,
  programmedActivitiesCursor: 0,
  selectedMirror: null,
  selectedHistoricMirror: null,
  selectedActivity: null,
  isLoadingActivities: false,
  lastMirrorAcessed: '',
  loadingMirror: false,
  loadingHistoricMirror: false,
  reporterAutoScrollTarget: null,
  timeAutoScrollTarget: null,
  dateChangedMirror: null,
  isVisibilityBodyMirror:
    typeof JSON.parse(localStorage.getItem('@nh-visibility-body')) !== 'boolean'
      ? true
      : JSON.parse(localStorage.getItem('@nh-visibility-body')),
  jobStatusCheckMirror: false,
};

const sortItemsByObjKey = (items, key) => {
  return items.sort((firstElem, secondElem) => {
    return firstElem[key] < secondElem[key]
      ? -1
      : firstElem[key] > secondElem[key]
      ? 1
      : 0;
  });
};

const searchForActivity = (mirror, username) => {
  if (!mirror) {
    return false;
  }
  const match = mirror.find(
    item =>
      item.reporter && item.reporter.toUpperCase() === username.toUpperCase(),
  );

  return !!match;
};

const getters = {
  filteredMirror: state =>
    (state.selectedMirror || []).filter(item => {
      const normType = normalizeType(item.item_type);
      return state.activeFilters.includes(normType);
    }),
  filteredHistoricMirror: state =>
    (state.selectedHistoricMirror || []).filter(item => {
      const normType = normalizeType(item.item_type);
      return state.activeFilters.includes(normType);
    }),
  userHasActivity: () => (mirror, username) => {
    searchForActivity(mirror, username);
  },
};

function handleRequestError(error, dispatch) {
  if (error.response.status === 401) {
    router.push('/login-force');
  }
  if (error.response.status === 403) {
    dispatch(
      'snackbar/showSnackbarWithMessage',
      {
        message: 'Você não possui permissão para acessar esse espelho',
        type: 'error',
      },
      { root: true },
    );
    router.push('/', {
      query: {
        view: 'MIRROR',
        content: 'story',
      },
    });
  }
}

const actions = {
  handleLastMirror({ commit }, value) {
    commit('SET_LAST_MIRROR', value);
  },
  getPrograms({ commit, rootState: root }) {
    const token = root.user.currentUser.token;
    root.pautaClient.getPrograms(token).then(programs => {
      if (programs) {
        commit('SET_PROGRAMS', sortItemsByObjKey(programs, 'program_name'));
      }
    });
  },

  getProgrammedActivities({ commit, rootState: root }) {
    const token = root.user.currentUser.token;
    const userName = root.user.currentUser.username;

    commit('SET_LOADING_ACTIVITIES', true);

    root.pautaClient
      .getUsersProgrammedActivities(token, userName)
      .then(activities => {
        commit('SET_PROGRAMMED_ACTIVITIES', activities || []);

        commit('SET_LOADING_ACTIVITIES', false);
      })
      .catch(error => {
        if (error.response.status === 401) {
          router.push('/login-force');
        }
      });
  },
  selectHistoricMirror({ commit, rootState: root }, payload) {
    const token = root.user.currentUser.token;
    commit('SET_LOADING_HISTORIC_MIRROR', true);
    root.pautaClient
      .getHistoricMirror(
        token,
        payload.mirrorShortName,
        payload.place,
        payload.filterDate,
      )
      .then(items => {
        if (items) {
          commit(
            'SELECT_HISTORIC_MIRROR',
            items.map(item => ({ ...item, is_historic: true })),
          );
        }
        commit('SET_LOADING_HISTORIC_MIRROR', false);
      })
      .catch(error => {
        if (error.response.status === 401) router.push('/login-force');
      });
  },
  selectMirror({ commit, rootState: root, dispatch }, payload) {
    const token = root.user.currentUser.token;
    dispatch('stopJobCheckAndUpdateSelectedMirror');
    commit('SET_LOADING_MIRROR', true);
    root.pautaClient
      .getMirrorByName(token, payload.programName, payload.place)
      .then(items => {
        if (items) {
          commit('SELECT_MIRROR', items);
        }
        commit('SET_LOADING_MIRROR', false);
        dispatch('startJobCheckAndUpdateSelectedMirror');
      })
      .catch(error => {
        handleRequestError(error, dispatch);
      })
      .finally(() => {
        setTimeout(() => {
          commit('SET_LOADING_UPDATE_MIRROR', false);
        }, 2000);
      });
  },
  getReporterAutoScrollTarget({ commit }, username, mirror) {
    let reporterTarget = null;
    let now = new Date();

    for (const [index, activity] of mirror.entries()) {
      let airTime = new Date(
        parseInt(activity.start_time + 3 * 60 * 60) * 1000,
      );
      if (
        timeFormat(airTime) > timeFormat(now) &&
        activity.reporter &&
        activity.reporter.toUpperCase() === username.toUpperCase()
      ) {
        reporterTarget = index;
        break;
      }
    }
    commit('SET_REPORTER_AUTO_SCROLL_TARGET', reporterTarget);
  },
  getTimeAutoScrollTarget({ commit }, mirror) {
    let timeTarget = null;
    let now = new Date();

    for (const [index, activity] of mirror.entries()) {
      let airTime = new Date(
        parseInt(activity.start_time + 3 * 60 * 60) * 1000,
      );
      if (timeFormat(airTime) > timeFormat(now)) {
        timeTarget = index;
        break;
      }
    }
    commit('SET_TIME_AUTO_SCROLL_TARGET', timeTarget);
  },
  resetAutoScrollTarget({ commit }) {
    commit('SET_REPORTER_AUTO_SCROLL_TARGET', null);
    commit('SET_TIME_AUTO_SCROLL_TARGET', null);
  },
  checkForUpdateOnMirror({ commit, rootState: root }, payload) {
    const token = root.user.currentUser.token;
    return root.pautaClient
      .checkForUpdateOnMirror(token, payload.programName, payload.place)
      .then(result => {
        commit('SET_HAS_UPDATE', result && result.isChanged);
      })
      .catch(error => {
        if (error.response.status === 401) {
          router.push('/login-force');
        }
        commit('SET_HAS_UPDATE', false);
      });
  },

  setSelectedActivity({ commit }, { slug }) {
    const hasSelectedNewSlug =
      router.currentRoute.query.selectedSlugId !== slug.id;
    if (hasSelectedNewSlug) {
      router.push({ query: { selectedSlugId: slug.id } });
    }
    commit('SET_SELECTED_ACTIVITY', slug);
  },
  getSystemFoldersHistoricMirrorTree({ commit, rootState }) {
    commit('SET_SYSTEM_FOLDERS_MIRROR_HISTORIC_TREE_LOADING', true);
    const token = rootState.user.currentUser.token;
    rootState.pautaClient
      .getSystemFoldersMirrorHistoricTree(token)
      .then(data => {
        commit('UPDATE_SYSTEM_FOLDERS_MIRROR_HISTORIC_TREE', data);
        commit('SET_SYSTEM_FOLDERS_MIRROR_HISTORIC_TREE_LOADING', false);
      });
  },
  setSelectNameHistoricMirror({ commit }, { program_name }) {
    commit('SET_SELECT_NAME_HISTORIC_MIRROR', program_name);
  },
  getSystemHistoricMirror(
    { commit, rootState },
    { places_and_folders, from_date, to_date },
  ) {
    const token = rootState.user.currentUser.token;
    const {
      systemHistoricMirrorAfter: currentAfter,
      systemHistoricMirror: currentSystemHistoricMirror,
      canLoadSystemHistoricMirror,
    } = rootState.mirror || {};

    if (canLoadSystemHistoricMirror) {
      commit('SET_LOADING_SYSTEM_MIRROR_HISTORIC', true);
      rootState.pautaClient
        .getSystemHistoricMirror(token, {
          places_and_folders: places_and_folders,
          from_date: from_date,
          to_date: to_date,
          after: currentAfter,
        })
        .then(({ data, after }) => {
          commit('SET_SYSTEM_MIRROR_HISTORIC', [
            ...currentSystemHistoricMirror,
            ...data,
          ]);
          if (!after || !after.processing_date || !after.path || !after.place)
            commit('SET_CAN_LOAD_SYSTEM_MIRROR_HISTORIC', false);
          else commit('SET_SYSTEM_MIRROR_HISTORIC_AFTER', after);
        })
        // eslint-disable-next-line no-unused-vars
        .catch(error => {
          if (error.response.status === 401) {
            router.push('/login-force');
          }
          commit('SET_ERROR_MIRROR_HISTORIC', true);
          commit('SET_LOADING_SYSTEM_MIRROR_HISTORIC', false);
        });
    }
  },

  setLoadingSystemHistoricMirror({ commit }) {
    commit('SET_LOADING_SYSTEM_MIRROR_HISTORIC', true);
  },

  resetControlSystemHistoricMirror({ commit }) {
    commit('SET_SYSTEM_MIRROR_HISTORIC_AFTER', null);
    commit('SET_CAN_LOAD_SYSTEM_MIRROR_HISTORIC', true);
    commit('SET_SYSTEM_MIRROR_HISTORIC', []);
  },
  setVisibilityChangeBody({ commit, rootState }) {
    localStorage.setItem(
      '@nh-visibility-body',
      !rootState.mirror.isVisibilityBodyMirror,
    );

    commit(
      'SET_VISIBILITY_BODY_CHANGE_MIRROR',
      !rootState.mirror.isVisibilityBodyMirror,
    );
  },

  checkAndUpdateSelectedMirror({ commit, rootState: root, dispatch }, payload) {
    dispatch('stopJobCheckAndUpdateSelectedMirror');
    const token = root.user.currentUser.token;

    root.pautaClient
      .getMirrorByName(token, payload.programName, payload.place)
      .then(mirrorFromInews => {
        if (
          mirrorFromInews &&
          checkMirrorHasUpdated(state.selectedMirror, mirrorFromInews)
        ) {
          commit('SELECT_MIRROR', mirrorFromInews);
          commit('SET_LOADING_UPDATE_MIRROR', true);
        }
      })
      .catch(error => {
        commit('SET_LOADING_UPDATE_MIRROR', false);
        handleRequestError(error, dispatch);
      })
      .finally(() => {
        dispatch('startJobCheckAndUpdateSelectedMirror');
        setTimeout(() => {
          commit('SET_LOADING_UPDATE_MIRROR', false);
        }, 2000);
      });
  },
  startJobCheckAndUpdateSelectedMirror({ commit }) {
    commit('SET_JOB_STATUS_CHECK_MIRROR', true);
  },
  stopJobCheckAndUpdateSelectedMirror({ commit }) {
    commit('SET_JOB_STATUS_CHECK_MIRROR', false);
  },
};

const mutations = {
  SET_ERROR(state, payload) {
    state.requestFailed = payload;
  },
  SET_VISIBILITY_BODY_CHANGE_MIRROR(state, payload) {
    state.isVisibilityBodyMirror = payload;
  },
  SET_ACTIVE_FILTERS(state, payload) {
    state.activeFilters = payload;
  },
  SET_LAST_MIRROR(state, value) {
    state.lastMirrorAcessed = value;
  },
  SET_PROGRAMS(state, items) {
    state.programs = items;
  },
  SELECT_MIRROR(state, value) {
    state.selectedMirror = value;
    const { ingest_date } = value[0] || {};
    state.dateChangedMirror = dateTimeFormat(ingest_date);
  },
  SELECT_HISTORIC_MIRROR(state, value) {
    state.selectedHistoricMirror = value;
  },
  SET_PROGRAMMED_ACTIVITIES(state, values) {
    state.programmedActivities = values;
  },
  SET_SELECTED_ACTIVITY(state, value) {
    state.selectedActivity = { ...state.selectedActivity, ...value };
  },
  SET_LOADING_ACTIVITIES(state, value) {
    state.isLoadingActivities = value;
  },
  SET_LOADING_MIRROR(state, value) {
    state.loadingMirror = value;
  },
  SET_LOADING_HISTORIC_MIRROR(state, value) {
    state.loadingHistoricMirror = value;
  },
  SET_REPORTER_AUTO_SCROLL_TARGET(state, value) {
    state.reporterAutoScrollTarget = value;
  },
  SET_TIME_AUTO_SCROLL_TARGET(state, value) {
    state.timeAutoScrollTarget = value;
  },
  UPDATE_FEEDBACK_MESSAGE(state, value) {
    state.feedbackMessage = value;
  },
  SET_JOB_STATUS_CHECK_MIRROR(state, payload) {
    state.jobStatusCheckMirror = payload;
  },
  SET_LOADING_UPDATE_MIRROR(state, value) {
    state.loadingUpdateMirror = value;
  },
  SET_SYSTEM_FOLDERS_MIRROR_HISTORIC_TREE_LOADING(state, value) {
    state.loadingFoldersMirror = value;
  },
  UPDATE_SYSTEM_FOLDERS_MIRROR_HISTORIC_TREE(state, folders) {
    state.systemFoldersMirrorHistoric = folders;
  },
  SET_SYSTEM_MIRROR_HISTORIC(state, value) {
    state.systemHistoricMirror = value;
    state.errorGetMirrorHistoric = false;
    state.loadingSystemHistoricMirror = false;
  },
  SET_SYSTEM_MIRROR_HISTORIC_AFTER(state, value) {
    state.systemHistoricMirrorAfter = value;
  },
  SET_CAN_LOAD_SYSTEM_MIRROR_HISTORIC(state, value) {
    state.canLoadSystemHistoricMirror = value;
  },

  SET_LOADING_SYSTEM_MIRROR_HISTORIC(state, value) {
    state.loadingSystemHistoricMirror = value;
  },
  SET_ERROR_MIRROR_HISTORIC(state, value) {
    state.errorGetMirrorHistoric = value;
  },
  SET_SELECT_NAME_HISTORIC_MIRROR(state, value) {
    state.selectNameHistoricMirror = value;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};
